<template>
  <section id="courses" class="py-16 bg-gray-50">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <h2 class="text-[clamp(1.5rem,3vw,2.5rem)] font-bold text-dark">
          课程培训
        </h2>
        <p class="mt-4 text-gray-medium max-w-2xl mx-auto">
          精选各行业优质课程，满足不同学习需求，随时随地在线学习
        </p>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div
          class="loader w-8 h-8 border-4 border-gray-200 border-t-primary rounded-full"
        ></div>
        <span class="ml-3 text-gray-600">加载中...</span>
      </div>

      <!-- 错误提示 -->
      <div v-if="error" class="text-center py-6 mb-8">
        <div
          class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto"
        >
          <div class="flex items-center justify-center mb-2">
            <i
              class="fas fa-exclamation-triangle text-yellow-500 text-lg mr-2"
            ></i>
            <h3 class="text-sm font-semibold text-yellow-800">离线模式</h3>
          </div>
          <p class="text-yellow-700 text-xs">{{ error }}</p>
        </div>
      </div>

      <!-- 课程列表 -->
      <div
        v-if="!loading && courses.length > 0"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        <div
          v-for="course in courses"
          :key="course.course_id"
          @click="goToCourseDetail(course.course_id)"
          class="bg-white rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-custom group cursor-pointer"
        >
          <div class="relative">
            <img
              :src="$getImageUrl(course.course_image)"
              :alt="course.title"
              class="w-full h-48 object-cover group-hover:scale-105 transition-custom duration-500"
            />
            <div
              v-if="course.status"
              :class="getStatusClass(course.status)"
              class="absolute top-3 left-3 text-white text-xs px-2 py-1 rounded-full"
            >
              {{ course.status }}
            </div>
            <div
              class="absolute top-3 right-3 bg-dark/70 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm"
            >
              <i class="fas fa-play-circle mr-1"></i> {{ course.lessons }}课时
            </div>
          </div>
          <div class="p-5">
            <div class="flex items-center text-xs text-gray-medium mb-2">
              <span class="bg-primary/10 text-primary px-2 py-0.5 rounded">{{
                course.category
              }}</span>
              <span class="ml-2"
                ><i class="fas fa-user mr-1"></i>
                {{ course.students }}人学习</span
              >
            </div>
            <h3
              class="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-custom"
            >
              {{ course.title }}
            </h3>
            <p
              class="text-gray-medium text-sm mb-3 line-clamp-2 leading-relaxed"
            >
              {{ course.course_description || "暂无课程描述" }}
            </p>
            <div class="flex justify-between items-center pt-3 border-t">
              <button
                v-if="course.purchased"
                @click.stop="continueLearning(course.course_id, course)"
                class="text-primary font-medium hover:underline"
              >
              课程学习 <i class="fas fa-arrow-right ml-1 text-sm"></i>
              </button>
              <button
                v-else
                @click.stop="purchaseCourse(course.course_id)"
                class="bg-secondary text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-secondary/90 transition-custom"
              >
                立即购买
              </button>
              <div class="text-lg font-bold text-primary">
                ¥{{ formatCoursePrice(course) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div
        v-if="!loading && courses.length === 0 && !error"
        class="text-center py-12"
      >
        <i class="fas fa-graduation-cap text-gray-300 text-5xl mb-4"></i>
        <p class="text-gray-500">暂无课程数据</p>
      </div>

      <!-- 查看更多课程 -->
      <div class="mt-10 text-center">
        <button
          @click="goToCoursesPage"
          class="px-6 py-3 bg-white border border-gray-light rounded-lg text-gray-dark font-medium hover:bg-light transition-custom flex items-center mx-auto"
        >
          <span>查看更多课程</span>
          <i class="fas fa-arrow-right ml-2"></i>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { courseAPI } from "@/api/services";

export default {
  name: "CoursesSection",
  data() {
    return {
      courses: [],
      loading: false,
      error: null,
    };
  },
  async mounted() {
    await this.loadCourses();
  },
  methods: {
    async loadCourses() {
      this.loading = true;
      this.error = null;

      try {
        const response = await courseAPI.getCourses({
          page: 1,
          limit: 4,
          course_type: "normal",
        });
        this.courses = response.data;
      } catch (error) {
        console.error("加载课程失败:", error);
        this.error = error.msg || error.message || "服务器连接失败，无法加载课程数据";
        this.courses = [];
      } finally {
        this.loading = false;
      }
    },

    getStatusClass(status) {
      switch (status) {
        case "已购买":
          return "bg-success";
        case "热门":
          return "bg-warning";
        default:
          return "bg-primary";
      }
    },

    getCoursePrice(course) {
      if (!course) return 0;
      if (course.course_price !== undefined && course.course_price !== null) {
        return course.course_price;
      }
      if (course.price !== undefined && course.price !== null) {
        return course.price;
      }
      if (course.fee !== undefined && course.fee !== null) {
        return course.fee;
      }
      return 0;
    },

    formatCoursePrice(course) {
      const price = this.getCoursePrice(course);
      const numericPrice = Number(price);

      if (Number.isNaN(numericPrice)) {
        return price || 0;
      }

      return numericPrice % 1 === 0
        ? numericPrice.toString()
        : numericPrice.toFixed(2);
    },

    goToCourseDetail(courseId) {
      this.$router.push(`/course/${courseId}`);
    },

    goToCoursesPage() {
      this.$router.push("/courses");
    },

    purchaseCourse(courseId) {
      // 支付功能开发中，暂时禁用
      // this.$message.warning('支付功能开发中，敬请期待！');
      // return;
      
      // // 先检查是否已登录
      if (!this.$store.getters.isLoggedIn) {
        this.$message.warning('请先登录后再购买课程');
        this.$store.commit('SHOW_LOGIN_MODAL');
        return;
      }
      // 已登录，跳转到支付页面
      this.$router.push({ name: "payment", params: { id: courseId } });
    },

    continueLearning(courseId, course) {
      // 先检查是否已登录
      if (!this.$store.getters.isLoggedIn) {
        this.$message.warning('请先登录后再学习课程');
        this.$store.commit('SHOW_LOGIN_MODAL');
        return;
      }
      
      // 根据课程是否过期决定跳转页面
      if (course && course.expired === false) {
        // 课程已过期，跳转到课程详情页面
        this.$message.warning('课程已过期，请重新购买');
        this.$router.push({
          name: "course-detail",
          params: {
            id: courseId,
          },
        });
      } else {
        // 课程未过期，跳转到视频学习页面
        this.$router.push({
          name: "video-learning",
          params: {
            courseId: courseId,
          },
        });
      }
    },
  },
};
</script>

<style lang="less" scoped>
.transition-custom {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
